import { AxiosInstance, AxiosResponse } from "axios"
import {
  SourceCodeInventory,
  ExportMapFillResult,
  ExportMapFillOptions,
} from "types/sourceCodes.types"

/**
 * Export map fill API — proxied to the AI-converter by the Conductor with the
 * same auth as the AI questionnaire conversion.
 *
 * Two of these endpoints are async-capable: called with `ASync-Request: 10`
 * they either answer 200 with the real body or 202 with a job id to poll
 * (see hooks/core/useAsyncCapable).
 */

/**
 * Response envelope of /extract-source-codes
 *
 * A failure carries `success: false` with `error` (short code or reason) and
 * `message` (what to tell the user). The converter answers those as JSON and
 * the Conductor passes them through with HTTP 200, so a failed run arrives as
 * a RESOLVED response — callers must read `success`, not only catch.
 */
export interface ExtractSourceCodesResponse {
  success: boolean
  error?: string
  message?: string
  data: {
    inventory: SourceCodeInventory
    stats: Record<string, unknown>
  }
}

/**
 * Response envelope of /fill-export-map. Same failure shape as the extraction
 * envelope: `success: false` with `error` and `message`, over HTTP 200.
 */
export interface FillExportMapResponse {
  success: boolean
  error?: string
  message?: string
  data: ExportMapFillResult
}

/**
 * Extracts the source code inventory (question and option codes) from a DOCX
 * questionnaire. Same transport as the AI conversion: multipart with the file
 * in the `document` field.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param file - The DOCX file to read codes from
 * @param isAsync - Send `ASync-Request` so a long run answers 202 + job id
 * @returns 200 with the extraction response, or 202 with the async job id
 */
export async function extractSourceCodes(
  axiosInstance: AxiosInstance,
  file: File,
  isAsync: boolean
): Promise<AxiosResponse<ExtractSourceCodesResponse | string>> {
  const formData = new FormData()
  formData.append("document", file)

  const config: {
    headers: {
      "Content-Type": string
      "ASync-Request"?: number
    }
  } = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }

  if (isAsync) {
    config.headers["ASync-Request"] = 10
  }

  const response = await axiosInstance.post<ExtractSourceCodesResponse | string>(
    "/extract-source-codes",
    formData,
    config
  )

  return response
}

/**
 * Matches an export map CSV against a source code inventory and proposes
 * variable and category codes per row.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param exportMapCsv - Current export map as CSV (LABEL,TYPE,VARIABLE,VALUES)
 * @param inventory - Inventory from extractSourceCodes
 * @param options - Fill options (`boolVariablePattern` and `onlyEmpty`); the
 *   server skips rows that already carry a code when `onlyEmpty` is set, so it
 *   travels with the request as well as gating what the client applies. The
 *   optional naming patterns are sent only when set, so an unset one leaves the
 *   server on its own default rather than overriding it with an empty string
 * @param isAsync - Send `ASync-Request` so a long run answers 202 + job id
 * @returns 200 with the fill response, or 202 with the async job id
 */
export async function fillExportMap(
  axiosInstance: AxiosInstance,
  exportMapCsv: string,
  inventory: SourceCodeInventory,
  options: ExportMapFillOptions,
  isAsync: boolean
): Promise<AxiosResponse<FillExportMapResponse | string>> {
  const config: {
    headers: {
      "Content-Type": string
      "ASync-Request"?: number
    }
  } = {
    headers: {
      "Content-Type": "application/json",
    },
  }

  if (isAsync) {
    config.headers["ASync-Request"] = 10
  }

  const response = await axiosInstance.post<FillExportMapResponse | string>(
    "/fill-export-map",
    {
      exportMapCsv,
      inventory,
      options: {
        boolVariablePattern: options.boolVariablePattern,
        onlyEmpty: options.onlyEmpty,
        ...(options.otherTextPattern
          ? { otherTextPattern: options.otherTextPattern }
          : {}),
        ...(options.valueTwinPattern
          ? { valueTwinPattern: options.valueTwinPattern }
          : {}),
        ...(options.gridBrandPattern
          ? { gridBrandPattern: options.gridBrandPattern }
          : {}),
      },
    },
    config
  )

  return response
}

/**
 * Stores the extracted inventory with the project so a later fill can reuse it.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - Project to store the inventory under
 * @param inventory - Inventory to store (sent as JSON text)
 */
export async function saveSourceCodes(
  axiosInstance: AxiosInstance,
  projectId: string,
  inventory: SourceCodeInventory
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.post(
    `/sourcecodes-save?id=${projectId}`,
    JSON.stringify(inventory),
    {
      headers: { "Content-Type": "text/plain" },
    }
  )
  return response
}

/**
 * Loads the inventory stored with the project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - Project to load the inventory from
 * @returns The stored inventory, or null when the project has none (404)
 * @throws Any error other than a 404
 */
export async function getSourceCodes(
  axiosInstance: AxiosInstance,
  projectId: string
): Promise<SourceCodeInventory | null> {
  try {
    const response = await axiosInstance.get<string>(
      `/sourcecodes-get?id=${projectId}`,
      { responseType: "text" }
    )
    const body = response.data
    return (
      typeof body === "string" ? JSON.parse(body) : body
    ) as SourceCodeInventory
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response
      ?.status
    if (status === 404) return null
    throw error
  }
}
